/**
 * Curated qualification + subject + grade catalogue used by the
 * Predicted Grades picker (progressive profile).
 *
 * Kept in one place so `StudentProfilePage`, the shortlist
 * `ProfileCompleteness` widget, and any future admissions flow all
 * pull from the same list.
 */

export const QUALIFICATIONS = [
  { value: "a_level",             label: "A-Level (UK)" },
  { value: "ib_hl",               label: "IB Higher Level" },
  { value: "ib_sl",               label: "IB Standard Level" },
  { value: "btec",                label: "BTEC" },
  { value: "scottish_higher",     label: "Scottish Higher" },
  { value: "scottish_adv_higher", label: "Scottish Advanced Higher" },
];

// Grades allowed per qualification — surfaces as a <select> so
// students can't type nonsense values.
export const GRADES_BY_QUALIFICATION = {
  a_level:             ["A*", "A", "B", "C", "D", "E"],
  ib_hl:               ["7", "6", "5", "4", "3", "2", "1"],
  ib_sl:               ["7", "6", "5", "4", "3", "2", "1"],
  btec:                ["D*D*D*", "D*D*D", "D*DD", "DDD", "DDM", "DMM", "MMM", "MMP", "MPP", "PPP", "D*", "D", "M", "P"],
  scottish_higher:     ["A", "B", "C", "D"],
  scottish_adv_higher: ["A", "B", "C", "D"],
};

// Curated subject catalogue per qualification. Free typing is still
// allowed (autocomplete filters against this list) — the list is
// there to give students strong defaults, not to restrict them.
const A_LEVEL_SUBJECTS = [
  "Mathematics", "Further Mathematics",
  "English Literature", "English Language", "English Language & Literature",
  "Physics", "Chemistry", "Biology",
  "History", "Geography", "Economics", "Business", "Accounting",
  "Psychology", "Sociology", "Politics", "Philosophy", "Religious Studies",
  "French", "Spanish", "German", "Italian", "Mandarin", "Latin",
  "Classical Civilisation", "Ancient History",
  "Computer Science", "Electronics",
  "Art & Design", "Design & Technology", "Photography",
  "Music", "Music Technology", "Drama & Theatre", "Film Studies", "Media Studies",
  "Physical Education", "Law", "Environmental Science", "Geology",
];

const IB_SUBJECTS = [
  "Mathematics: Analysis & Approaches",
  "Mathematics: Applications & Interpretation",
  "English A: Literature", "English A: Language & Literature", "English B",
  "Physics", "Chemistry", "Biology", "Environmental Systems & Societies",
  "History", "Geography", "Economics", "Business Management", "Global Politics",
  "Psychology", "Philosophy",
  "French B", "Spanish B", "German B", "Mandarin B", "Ab Initio Language",
  "Computer Science",
  "Visual Arts", "Music", "Theatre", "Film",
  "Sports, Exercise & Health Science",
];

const BTEC_SUBJECTS = [
  "Applied Science", "Business", "Engineering", "Health & Social Care",
  "Information Technology", "Creative Digital Media Production",
  "Sport", "Performing Arts", "Art & Design",
  "Public Services", "Travel & Tourism", "Uniformed Protective Services",
];

const SCOTTISH_SUBJECTS = [
  "Mathematics", "Applications of Mathematics",
  "English", "Physics", "Chemistry", "Biology",
  "History", "Geography", "Modern Studies", "Business Management", "Economics",
  "Computing Science", "Accounting",
  "French", "Spanish", "German", "Gaelic", "Latin",
  "Art & Design", "Music", "Drama", "Media",
  "Physical Education", "Design & Manufacture",
];

export const SUBJECTS_BY_QUALIFICATION = {
  a_level:             A_LEVEL_SUBJECTS,
  ib_hl:               IB_SUBJECTS,
  ib_sl:               IB_SUBJECTS,
  btec:                BTEC_SUBJECTS,
  scottish_higher:     SCOTTISH_SUBJECTS,
  scottish_adv_higher: SCOTTISH_SUBJECTS,
};

export const QUALIFICATION_LABEL = Object.fromEntries(
  QUALIFICATIONS.map(q => [q.value, q.label])
);
